import { Anchor, Box, Group, Stack, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Button } from 'src/design-system/Button';
import { Modal } from 'src/design-system/Modal';
import { PickerSectionBox } from 'src/design-system/PickerSectionBox';
import { PersonRow } from 'src/design-system/SelectPeopleModal';
import { Tooltip } from 'src/design-system/Tooltip';
import {
  isStaleReconciliation,
  useAttendeeReconciliation,
} from 'src/meetings/hooks/useAttendeeReconciliation';
import type {
  AttendeeDifference,
  AttendeeDifferenceReason,
  CalendarEvent,
} from 'src/meetings/models';
import type { MeetingScope } from 'src/meetings/scope';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = { opened: boolean; onClose: () => void; event: CalendarEvent; scope?: MeetingScope };

type Selection = Record<string, boolean>;

const REASON_TEXT: Record<AttendeeDifferenceReason, string> = {
  removed_in_afternoon: 'On the calendar, but removed in Afternoon',
  added_to_calendar: 'Newly added to the calendar',
  added_in_afternoon: 'Added in Afternoon, not on the calendar',
  removed_from_calendar: 'Removed from the calendar',
};

const SHORTCUTS: {
  label: string;
  ariaLabel: string;
  tooltip: string;
  pick: (difference: AttendeeDifference) => boolean;
}[] = [
  {
    label: 'Calendar',
    ariaLabel: 'Select everyone as on the calendar',
    tooltip: "Use the calendar's guest list for everyone",
    pick: difference => difference.onCalendar,
  },
  {
    label: 'Afternoon',
    ariaLabel: 'Select everyone as in Afternoon',
    tooltip: 'Keep everyone as they are in Afternoon',
    pick: isAttendee,
  },
];

export function CalendarGuestChangesModal({ opened, onClose, event, scope }: Props) {
  const differences = event.attendeeDifferences ?? [];
  const differingEmails = differences.map(difference => difference.email).join('\n');
  const [selection, setSelection] = useState<Selection>(() => selectionOf(differences, isAttendee));
  const [isStale, setIsStale] = useState(false);
  const { mutateAsync, isPending } = useAttendeeReconciliation(event.id, scope);

  useEffect(() => {
    setSelection(selectionOf(differences, isAttendee));
    if (!opened) setIsStale(false);
  }, [opened, differingEmails]);

  // A save still in flight decides the meeting's attendees, so the review stays open until it settles.
  const close = () => {
    if (!isPending) onClose();
  };

  const save = async () => {
    try {
      await mutateAsync({
        decisions: differences.map(difference => ({
          email: difference.email,
          isAttendee: selection[difference.email] ?? false,
        })),
      });
      onClose();
    } catch (error) {
      if (isStaleReconciliation(error)) {
        setIsStale(true);
      } else {
        notifyAndLogError(
          'Failed to update attendees',
          'Could not update the meeting attendees. Please try again.',
          error,
        );
      }
    }
  };

  return (
    <Modal opened={opened} onClose={close} size={440} title="The calendar's guest list changed">
      <Text size="sm" c="dimmed" mb="lg">
        Choose who should be on this meeting.
      </Text>
      {isStale && (
        <Text size="sm" c="error.6" mb="sm" role="alert">
          This meeting changed while you were reviewing it. Check the list again.
        </Text>
      )}
      <PickerSectionBox>
        <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
          Attendees
        </Text>
        <Stack gap="xxxs" mt="xs">
          {differences.map(difference => (
            <PersonRow
              key={difference.email}
              row={{
                id: difference.email,
                name: difference.name,
                role: '',
                secondaryLabel: (
                  <Text size="xs" c="gray.6">
                    {REASON_TEXT[difference.reason]}
                  </Text>
                ),
                checked: selection[difference.email] ?? false,
                onToggle: () =>
                  setSelection(current => ({
                    ...current,
                    [difference.email]: !current[difference.email],
                  })),
                disabled: isPending,
                ariaLabel: `${difference.name} on this meeting`,
              }}
            />
          ))}
        </Stack>
      </PickerSectionBox>
      <Group justify="flex-end" gap="xs" mt="xs">
        <Text size="xs" c="gray.6">
          Select
        </Text>
        {SHORTCUTS.map(({ label, ariaLabel, tooltip, pick }) => (
          <Tooltip key={label} label={tooltip} position="top-end">
            <Anchor
              component="button"
              type="button"
              size="xs"
              fw={FONT_WEIGHTS.semibold}
              c={isPending ? 'gray.5' : 'brand.6'}
              underline="always"
              aria-label={ariaLabel}
              disabled={isPending}
              onClick={() => setSelection(selectionOf(differences, pick))}
            >
              {label}
            </Anchor>
          </Tooltip>
        ))}
      </Group>
      <Box mt="lg">
        <Button fullWidth onClick={save} loading={isPending} disabled={isPending}>
          Save
        </Button>
      </Box>
    </Modal>
  );
}

// The calendar's changes are already applied, so saving untouched keeps everyone as they are.
function isAttendee(difference: AttendeeDifference): boolean {
  return difference.isAttendee;
}

function selectionOf(
  differences: AttendeeDifference[],
  pick: (difference: AttendeeDifference) => boolean,
): Selection {
  return Object.fromEntries(differences.map(difference => [difference.email, pick(difference)]));
}
